import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Zap, Clock, Trophy, type LucideIcon } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import type { SectionBucket } from '../../../src/challenges/types/challenges.types';
import {
  getArenaSectionHeaderStyles,
  getArenaSectionHeaderIconColor,
} from './ArenaSectionHeader.styles';

type Urgency = 'urgent' | 'live' | 'soon' | 'active' | 'idle' | 'done';

const BUCKET_URGENCY: Record<SectionBucket, Urgency> = {
  yourMove: 'urgent',
  live: 'live',
  active: 'active',
  waitingForFriends: 'idle',
  waitingForTaker: 'idle',
  waitingForOpponent: 'idle',
  finished: 'done',
};

const URGENCY_ICON: Record<Urgency, LucideIcon> = {
  urgent: Zap,
  live: Zap,
  soon: Clock,
  active: Clock,
  idle: Clock,
  done: Trophy,
};

/** Text variant per urgency — status uses textOnStatusBadge (adapts to dark mode on colored bg) */
const URGENCY_TEXT_VARIANT: Record<Urgency, 'light' | 'dark' | 'status'> = {
  urgent: 'light',
  live: 'light',
  soon: 'light',
  active: 'status',
  idle: 'dark',
  /** Grey pill like idle; white label like primary/live pills */
  done: 'light',
};

interface ArenaSectionHeaderProps {
  sectionBucket: SectionBucket;
  title: string;
  count?: number;
}

export const ArenaSectionHeader: React.FC<ArenaSectionHeaderProps> = ({
  sectionBucket,
  title,
  count,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getArenaSectionHeaderStyles(theme), [theme]);
  const urgency = BUCKET_URGENCY[sectionBucket];
  const IconComponent = URGENCY_ICON[urgency];
  const textVariant = URGENCY_TEXT_VARIANT[urgency];

  const accentBarStyle = {
    urgent: styles.accentBarUrgent,
    live: styles.accentBarLive,
    soon: styles.accentBarSoon,
    active: styles.accentBarActive,
    idle: styles.accentBarIdle,
    done: styles.accentBarDone,
  }[urgency];

  const badgeStyle = {
    urgent: styles.badgeUrgent,
    live: styles.badgeLive,
    soon: styles.badgeSoon,
    active: styles.badgeActive,
    idle: styles.badgeIdle,
    done: styles.badgeDone,
  }[urgency];

  const titleStyle = textVariant === 'dark' ? styles.badgeTitleDark : textVariant === 'status' ? styles.badgeTitleStatus : styles.badgeTitleLight;
  const iconColor = getArenaSectionHeaderIconColor(theme, textVariant);

  return (
    <View style={styles.container}>
      <View style={[styles.accentBar, accentBarStyle]} />
      {/* Title lives inside the badge — exact design match */}
      <View style={[styles.badge, badgeStyle]}>
        {urgency === 'live' && <View style={styles.liveDot} />}
        <IconComponent size={11} color={iconColor} />
        <Text style={[styles.badgeTitle, titleStyle]}>{title.toUpperCase()}</Text>
      </View>
      {count != null && count > 0 && (
        <Text style={styles.count}>{count}</Text>
      )}
    </View>
  );
};
